import React from "react";
import { Helmet } from "react-helmet-async";
import PropTypes from "prop-types";
import {
  SUPPORTED_LANGUAGES,
  buildLocalizedPath,
  stripLangFromPath,
  getRouteKeyForPath,
  LOCALIZED_ROUTE_MAP,
} from "../../utils/routes";

const DEFAULT_SITE_URL = "https://coldexperience.se";
const DEFAULT_LANGUAGE = SUPPORTED_LANGUAGES[0];

const toRelativePath = (value, baseUrl) => {
  if (!value) {
    return null;
  }

  if (/^https?:\/\//i.test(value)) {
    try {
      const parsed = new URL(value, baseUrl || DEFAULT_SITE_URL);
      return parsed.pathname || "/";
    } catch (error) {
      return null;
    }
  }

  return value;
};

const normalizePath = (value) => {
  if (!value || value === "/") {
    return "/";
  }

  let normalized = value.startsWith("/") ? value : `/${value}`;

  if (normalized.length > 1) {
    normalized = normalized.replace(/\/+$/, "");
  }

  return normalized || "/";
};

const buildAbsoluteUrl = (baseUrl, pathname) => {
  const base = (baseUrl || DEFAULT_SITE_URL).replace(/\/+$/, "");
  const path = pathname?.startsWith("/") ? pathname : `/${pathname ?? ""}`;
  return `${base}${path}`;
};

const resolveDescriptor = (routeKey, relativePath) => {
  if (routeKey) {
    return routeKey;
  }

  const pathWithoutLang = stripLangFromPath(relativePath || "/");
  const detectedKey = getRouteKeyForPath(pathWithoutLang);

  if (detectedKey) {
    return detectedKey;
  }

  return normalizePath(pathWithoutLang);
};

const buildLanguageHref = (descriptor, language) => {
  if (descriptor && LOCALIZED_ROUTE_MAP[descriptor]) {
    return buildLocalizedPath(language, descriptor);
  }

  return buildLocalizedPath(language, descriptor || "/");
};

const SeoAlternateLinks = ({
  routeKey,
  canonicalPath,
  siteUrl,
  includeXDefault = true,
}) => {
  const baseSiteUrl = siteUrl || process.env.REACT_APP_SITE_URL || DEFAULT_SITE_URL;
  const resolvedCanonicalRelative =
    toRelativePath(canonicalPath, baseSiteUrl) ||
    (routeKey ? buildLocalizedPath(DEFAULT_LANGUAGE, routeKey) : `/${DEFAULT_LANGUAGE}/`);

  const descriptor = resolveDescriptor(routeKey, resolvedCanonicalRelative);
  const alternates = SUPPORTED_LANGUAGES.map((language) => {
    const localizedPath = buildLanguageHref(descriptor, language);
    const href = buildAbsoluteUrl(baseSiteUrl, localizedPath);
    return (
      <link
        key={language}
        rel="alternate"
        hrefLang={language}
        href={href}
      />
    );
  });

  const xDefaultHref =
    includeXDefault &&
    buildAbsoluteUrl(
      baseSiteUrl,
      buildLanguageHref(descriptor, DEFAULT_LANGUAGE)
    );

  return (
    <Helmet>
      {alternates}
      {includeXDefault && (
        <link rel="alternate" hrefLang="x-default" href={xDefaultHref} />
      )}
    </Helmet>
  );
};

SeoAlternateLinks.propTypes = {
  routeKey: PropTypes.string,
  canonicalPath: PropTypes.string,
  siteUrl: PropTypes.string,
  includeXDefault: PropTypes.bool,
};

export default SeoAlternateLinks;
